import { useEffect, useState } from 'react';

export interface PageThumbnailContent {
  html: string;
  width: number;
  backgroundColor: string;
}

const PREVIEW_DEBOUNCE_MS = 200;
const PAGE_SELECTOR = '.block-node-view, .section-node-view, .investment-group-view';

/** Browser-rendered copies retain the page's fonts, artwork and layout at any zoom. */
function copyPage(element: HTMLElement): PageThumbnailContent {
  const copy = element.cloneNode(true) as HTMLElement;
  const pageStyle = getComputedStyle(element);
  for (const property of ['font-family', 'font-size', 'font-weight', 'line-height', 'color']) {
    copy.style.setProperty(property, pageStyle.getPropertyValue(property));
  }
  const fields = element.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('input, textarea');
  const copiedFields = copy.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('input, textarea');

  fields.forEach((field, index) => {
    if (field instanceof HTMLInputElement && ['checkbox', 'radio', 'hidden'].includes(field.type)) {
      copiedFields[index].remove();
      return;
    }

    // Native text preserves textarea wrapping instead of flattening it into one line.
    const text = document.createElement('div');
    text.className = field.className;
    text.style.cssText = field.style.cssText;
    const style = getComputedStyle(field);
    for (const property of [
      'width', 'height', 'box-sizing', 'font-family', 'font-size', 'font-weight',
      'font-style', 'line-height', 'letter-spacing',
      'color', 'text-align', 'text-transform', 'padding', 'margin', 'border',
      'background-color', 'word-break', 'overflow-wrap',
    ]) {
      text.style.setProperty(property, style.getPropertyValue(property));
    }
    text.style.whiteSpace = field instanceof HTMLTextAreaElement ? 'pre-wrap' : 'nowrap';
    text.style.overflow = 'hidden';
    text.textContent = field.value;
    copiedFields[index].replaceWith(text);
  });

  copy.querySelectorAll('[data-html2canvas-ignore], button, .column-resize-handle').forEach((control) => control.remove());
  for (const node of [copy, ...copy.querySelectorAll<HTMLElement>('*')]) {
    node.removeAttribute('contenteditable');
    node.removeAttribute('data-quote-page-selected');
    node.classList.remove('is-selected', 'ProseMirror-selectednode');
  }

  return {
    html: copy.outerHTML,
    // Computed width retains fractional document coordinates at every editor zoom.
    width: parseFloat(pageStyle.width),
    backgroundColor: pageStyle.backgroundColor,
  };
}

export function usePageThumbnails(blockIds: string[]) {
  const [thumbnails, setThumbnails] = useState<Record<string, PageThumbnailContent>>({});
  const blockIdsKey = blockIds.join(',');

  useEffect(() => {
    let active = true;
    let timer: number | undefined;
    let retry: number | undefined;
    let editor: HTMLElement | null = null;
    const ids = new Set(blockIdsKey.split(','));

    const update = () => {
      if (!active || !editor) return;
      const next: Record<string, PageThumbnailContent> = {};
      editor.querySelectorAll<HTMLElement>(PAGE_SELECTOR).forEach((element) => {
        const id = element.dataset.blockId;
        if (id && ids.has(id) && element.offsetWidth > 0) next[id] = copyPage(element);
      });
      setThumbnails((current) => {
        let unchanged = Object.keys(current).length === Object.keys(next).length;
        for (const [id, page] of Object.entries(next)) {
          if (current[id]?.html === page.html && current[id]?.width === page.width &&
              current[id]?.backgroundColor === page.backgroundColor) {
            next[id] = current[id];
          } else {
            unchanged = false;
          }
        }
        return unchanged ? current : next;
      });
    };
    const schedule = () => {
      if (!active) return;
      window.clearTimeout(timer);
      timer = window.setTimeout(update, PREVIEW_DEBOUNCE_MS);
    };
    const observer = new MutationObserver(schedule);
    const observe = () => {
      editor = document.querySelector<HTMLElement>('.editor-page-container .tiptap-editor');
      if (!editor) {
        retry = window.setTimeout(observe, PREVIEW_DEBOUNCE_MS);
        return;
      }
      observer.observe(editor, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['style', 'class', 'src'],
      });
      editor.addEventListener('input', schedule);
      schedule();
    };
    observe();
    void document.fonts.ready.then(schedule);

    return () => {
      active = false;
      observer.disconnect();
      editor?.removeEventListener('input', schedule);
      window.clearTimeout(timer);
      window.clearTimeout(retry);
    };
  }, [blockIdsKey]);

  return thumbnails;
}
